import React from 'react'
import { Card, CardContent } from '../ui'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import {
  ASSET_CATEGORY_VIEWS,
  assetCategoryI18nKey,
  assetMatchesCategoryView,
  type AssetCategoryView,
} from '../../constants/assetTaxonomy'
import {
  fetchAssetOperationalSummaries,
  fetchAssetOperationalSummary,
  type AssetOperationalSummaryRow,
} from '../../services/assetOperationalApi'
import {
  fetchInformationSystems,
  linkAssetToSystem,
  unlinkAssetFromSystem,
  type InformationSystemSummary,
} from '../../services/informationSystemsApi'
import AssetOperationalRow from './AssetOperationalRow'
import AssetPassportPanel, { AssetPassportPanelEmptyState } from './AssetPassportPanel'
import {
  type AssetPanelContext,
  assetPanelContextFor,
  assetDrillDownToContext,
} from './assetPanelContext'
import isStyles from '../../styles/components/assets/AssetsInformationSystems.module.scss'
import intelStyles from '../../styles/components/assets/AssetsInventoryIntelligence.module.scss'

export interface InventoryAssetRef {
  id: string
  assetId?: string
  name: string
  type: string
  environment?: string
  criticality?: string
  ipAddress?: string
  networkTarget?: string
  details?: Record<string, unknown>
}

interface Props {
  assets: InventoryAssetRef[]
  searchQuery?: string
  categoryView?: AssetCategoryView | ''
  onCategoryViewChange?: (view: AssetCategoryView | '') => void
  onEdit?: (assetId: string) => void
  onScan?: (asset: InventoryAssetRef) => void
  onOpenSystem?: (systemId: string) => void
  filterAssetIds?: Set<string> | null
}

function assetKey(a: InventoryAssetRef): string {
  return String(a.id || a.assetId || '')
}

export default function AssetInventoryIntelligenceWorkspace({
  assets,
  searchQuery = '',
  categoryView = '',
  onCategoryViewChange,
  onEdit,
  onScan,
  onOpenSystem,
  filterAssetIds,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [rows, setRows] = React.useState<AssetOperationalSummaryRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [selectedId, setSelectedId] = React.useState<string | null>(null)
  const [detail, setDetail] = React.useState<Awaited<ReturnType<typeof fetchAssetOperationalSummary>> | null>(null)
  const [panelContext, setPanelContext] = React.useState<AssetPanelContext | null>(null)
  const [localCategory, setLocalCategory] = React.useState<AssetCategoryView | ''>(categoryView)
  const [informationSystems, setInformationSystems] = React.useState<InformationSystemSummary[]>([])
  const [linkingToSystem, setLinkingToSystem] = React.useState(false)

  const activeCategory = onCategoryViewChange ? categoryView : localCategory
  const setCategory = onCategoryViewChange || setLocalCategory

  const loadInformationSystems = React.useCallback(async () => {
    if (!token) return
    try {
      const list = await fetchInformationSystems(token, orgId)
      setInformationSystems(Array.isArray(list) ? list : [])
    } catch {
      setInformationSystems([])
    }
  }, [token, orgId])

  React.useEffect(() => {
    void loadInformationSystems()
  }, [loadInformationSystems])

  const loadList = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchAssetOperationalSummaries(token, orgId)
      setRows(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.error.fetchFailed', 'Failed to load assets'))
    } finally {
      setLoading(false)
    }
  }, [token, orgId, t])

  const loadDetail = React.useCallback(
    async (id: string) => {
      if (!token) return
      const d = await fetchAssetOperationalSummary(token, id, orgId)
      setDetail(d)
    },
    [token, orgId]
  )

  React.useEffect(() => {
    loadList()
  }, [loadList])

  const openAsset = React.useCallback(
    (id: string, kind: AssetPanelContext['kind'] = 'overview') => {
      setSelectedId(id)
      setPanelContext(assetPanelContextFor(id, kind === 'fullPassport' ? 'fullPassport' : (kind as import('./assetPanelContext').AssetContextNavKind)))
      loadDetail(id)
    },
    [loadDetail]
  )

  const openContext = React.useCallback(
    (ctx: AssetPanelContext) => {
      setPanelContext(ctx)
      if (selectedId !== String(ctx.assetId) || !detail) {
        setSelectedId(String(ctx.assetId))
        loadDetail(String(ctx.assetId))
      }
    },
    [selectedId, detail, loadDetail]
  )

  const refreshDetailAndList = React.useCallback(async () => {
    if (!selectedId) return
    await loadDetail(selectedId)
    await loadList()
  }, [selectedId, loadDetail, loadList])

  const mergedRows = React.useMemo(() => {
    const byId = new Map(rows.map((r) => [String(r.assetId), r]))
    return assets.map((a) => {
      const id = assetKey(a)
      const op = byId.get(id)
      if (op) {
        return {
          ...op,
          name: op.name || a.name,
          type: op.type || a.type,
          environment: op.environment ?? a.environment ?? null,
          networkTarget: op.networkTarget || a.ipAddress || null,
        }
      }
      return {
        assetId: id,
        name: a.name,
        type: a.type,
        environment: a.environment ?? null,
        criticality: a.criticality ?? null,
        networkTarget: a.ipAddress || null,
        linkedSystemsCount: 0,
        dataCategories: [],
        vulnerabilityCount: 0,
        criticalVulnerabilityCount: 0,
        failedControlsCount: 0,
        openRisksCount: 0,
        topVulnerabilities: [],
        topFailedControls: [],
        topOpenRisks: [],
      } as AssetOperationalSummaryRow
    })
  }, [assets, rows])

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return mergedRows.filter((row) => {
      if (filterAssetIds && !filterAssetIds.has(String(row.assetId))) return false
      if (activeCategory && !assetMatchesCategoryView(row.type, activeCategory)) return false
      if (!q) return true
      return (
        row.name.toLowerCase().includes(q) ||
        String(row.type).toLowerCase().includes(q) ||
        (row.environment || '').toLowerCase().includes(q)
      )
    })
  }, [mergedRows, searchQuery, activeCategory, filterAssetIds])

  const selectedSummaryRow = selectedId
    ? filteredRows.find((r) => String(r.assetId) === selectedId) ?? null
    : null

  const passportPanelProps =
    detail && selectedId && panelContext
      ? {
          detail,
          summaryRow: selectedSummaryRow,
          panelContext,
          onContextChange: setPanelContext,
          onClose: () => {
            setSelectedId(null)
            setDetail(null)
            setPanelContext(null)
          },
          onRefresh: refreshDetailAndList,
          onEdit: onEdit ? () => onEdit(selectedId) : undefined,
          onScan:
            onScan && assets.find((a) => assetKey(a) === selectedId)
              ? () => {
                  const a = assets.find((x) => assetKey(x) === selectedId)
                  if (a) onScan(a)
                }
              : undefined,
          onOpenSystem,
          informationSystems,
          linkingToSystem,
          onLinkToSystem: async (systemId: string) => {
            if (!token || !selectedId) return
            setLinkingToSystem(true)
            try {
              await linkAssetToSystem(token, systemId, selectedId, orgId)
              await refreshDetailAndList()
              await loadInformationSystems()
            } finally {
              setLinkingToSystem(false)
            }
          },
          onUnlinkFromSystem: async (systemId: string) => {
            if (!token || !selectedId) return
            setLinkingToSystem(true)
            try {
              await unlinkAssetFromSystem(token, systemId, selectedId, orgId)
              await refreshDetailAndList()
              await loadInformationSystems()
            } finally {
              setLinkingToSystem(false)
            }
          },
        }
      : null

  if (loading && rows.length === 0) {
    return <p className={isStyles.loadingText}>{t('common.loading')}</p>
  }

  return (
    <div className={intelStyles.inventoryWorkspace}>
      <nav className={intelStyles.categoryNav} aria-label={t('assets.categoryViews.label', 'Category views')}>
        <button
          type="button"
          className={!activeCategory ? intelStyles.categoryChipActive : intelStyles.categoryChip}
          onClick={() => setCategory('')}
        >
          {t('assets.categoryViews.all', 'All')}
        </button>
        {ASSET_CATEGORY_VIEWS.map((view) => (
          <button
            key={view}
            type="button"
            className={activeCategory === view ? intelStyles.categoryChipActive : intelStyles.categoryChip}
            onClick={() => setCategory(view)}
          >
            {t(assetCategoryI18nKey(view), view.replace(/([A-Z])/g, ' $1').trim())}
          </button>
        ))}
      </nav>

      {error ? <p className={isStyles.errorBanner}>{error}</p> : null}

      {filteredRows.length === 0 ? (
        <Card>
          <CardContent>
            <p className={isStyles.emptyHint}>{t('assets.assetPassport.empty', 'No assets match your filters.')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className={isStyles.systemsContentGrid}>
          <div className={isStyles.systemsList}>
            {filteredRows.map((row) => (
              <AssetOperationalRow
                key={String(row.assetId)}
                row={row}
                selected={selectedId === String(row.assetId)}
                onSelect={() => openAsset(String(row.assetId), 'overview')}
                onOpenPassport={() => openAsset(String(row.assetId), 'fullPassport')}
                onOpenGraph={() => openAsset(String(row.assetId), 'graph')}
                onOpenScans={() => openAsset(String(row.assetId), 'scans')}
                onOpenRelationships={() => openAsset(String(row.assetId), 'relationships')}
                onDrillDown={(target) => {
                  if (!target) return
                  openContext(
                    assetDrillDownToContext({
                      kind: target.kind,
                      assetId: row.assetId,
                      id: target.kind === 'risk' ? target.sourceId : target.id,
                      label: target.label,
                      sourceType: target.kind === 'risk' ? target.sourceType : undefined,
                      sourceId: target.kind === 'risk' ? target.sourceId : undefined,
                    })
                  )
                }}
                onEdit={onEdit ? () => onEdit(String(row.assetId)) : undefined}
                onScan={
                  onScan
                    ? () => {
                        const ref = assets.find((a) => assetKey(a) === String(row.assetId))
                        if (ref) onScan(ref)
                      }
                    : undefined
                }
              />
            ))}
            {passportPanelProps ? (
              <div className={isStyles.mobilePassportBelow}>
                <AssetPassportPanel {...passportPanelProps} />
              </div>
            ) : null}
          </div>
          <div className={isStyles.desktopPanelColumn}>
            {passportPanelProps ? <AssetPassportPanel {...passportPanelProps} /> : <AssetPassportPanelEmptyState />}
          </div>
        </div>
      )}
    </div>
  )
}
